// Globals
import debounce from 'lodash.debounce';
import { ChangeEvent, memo, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { changeQuery, getRedirects, selectQuery } from '../../feature';

// Components
import { Input, Select } from '@/ui';
import { StyledFilterRow } from './RedirectsFiltersContainerStyles';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Helpers
import { GetRedirectsDTO } from '../../dtos';
import { RedirectsFiltersFields } from '../../helpers/types';
import { getRedirectTypeOptions } from '../../helpers/getOptions';

type IRedirectsFiltersContainerProps = {
  children?: ReactNode;
  className?: string;
};

const RedirectsFiltersContainer = ({ children, className }: IRedirectsFiltersContainerProps) => {
  const { t } = useTranslation(['redirects']);
  const dispatch = useAppDispatch();
  const query = useAppState(selectQuery);
  const { canManage } = usePermissions();
  // The trailing actions column only exists when the admin can manage redirects (SEO).
  const canManageRedirects = canManage(Resource.SEO);

  const handleDebounceSearch = useMemo(
    () =>
      debounce(async (searchParams) => {
        const dto = new GetRedirectsDTO(searchParams);
        dispatch(getRedirects(dto));
      }, 500),
    [dispatch],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newQuery = { ...query, [name]: value || undefined };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleSelectFilter = (name: string) => (value: string) => {
    const newQuery = {
      ...query,
      [name]: value,
    };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        <th>
          <Select
            placeholder={t('redirect_status_code')}
            options={getRedirectTypeOptions()}
            onChange={handleSelectFilter(RedirectsFiltersFields.TYPE)}
            value={query?.[RedirectsFiltersFields.TYPE]?.toString()}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            placeholder={t('redirect_from')}
            name={RedirectsFiltersFields.SOURCE}
            onChange={handleSearch}
            value={query?.[RedirectsFiltersFields.SOURCE] || ''}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            placeholder={t('redirect_to')}
            name={RedirectsFiltersFields.TARGET}
            onChange={handleSearch}
            value={query?.[RedirectsFiltersFields.TARGET] || ''}
            size="large"
            allowClear
          />
        </th>
        {canManageRedirects && <th />}
      </StyledFilterRow>
    </thead>
  );
};

export default memo(RedirectsFiltersContainer);
